import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import fp from 'lodash/fp';
import {
  Schema,
  Button,
  FormControl,
  FormGroup,
  Form,
  Icon,
  SelectPicker,
  CheckPicker,
  ButtonToolbar,
  Checkbox
} from 'rsuite';
import { ControlLabel } from '../../../widgets/FormElements';

const { ArrayType, NumberType } = Schema.Types;

const DefaultFormValue = { locationId: [], defaultLocationId: null };
const FormModel = Schema.Model({
  locationId: ArrayType(NumberType()),
  defaultLocationId: NumberType()
});

const TrackingMethodLocationForm = ({
  onSubmit,
  isSaving,
  trackingMethodLocations,
  locationOptions
}) => {
  const formRef = useRef();
  const [formError, setFormError] = useState({});
  const [formValue, setFormValue] = useState(DefaultFormValue);
  const [checkAllLocations, setCheckAllLocations] = useState(false);
  const [indeterminateLocations, setIndeterminateLocations] = useState(false);

  const setLocationsCheckAllState = selectedLocations => {
    const hasSelectedLocations = fp.size(selectedLocations) > 0;
    const isAllLocationsSelected =
      fp.size(selectedLocations) === fp.size(locationOptions);
    setCheckAllLocations(hasSelectedLocations && isAllLocationsSelected);
    setIndeterminateLocations(hasSelectedLocations && !isAllLocationsSelected);
  };

  const availableLocationOptions = useMemo(
    () => {
      if (fp.isEmpty(formValue.locationId)) return [];
      return fp.filter(
        each => fp.includes(each.value, formValue.locationId),
        locationOptions
      );
    },
    [locationOptions, formValue.locationId]
  );

  const currentLocationIds = useMemo(
    () => trackingMethodLocations.map(fp.get('inventoryLocation.id')),
    [trackingMethodLocations]
  );

  const setDefaultLocations = () => {
    if (fp.isEmpty(trackingMethodLocations)) return;

    const defaultLocationId = fp.flow(
      fp.find('isDefault'),
      fp.get('inventoryLocation.id')
    )(trackingMethodLocations);

    setFormValue({
      ...formValue,
      locationId: currentLocationIds,
      defaultLocationId
    });
    setLocationsCheckAllState(currentLocationIds);
  };

  const handleFormChange = value => {
    setFormValue(value);
    setLocationsCheckAllState(value.locationId);
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  const handleSubmit = () => {
    if (!formRef.current.check()) {
      return;
    }
    const deleteLocationIds = fp.difference(
      currentLocationIds,
      formValue.locationId
    );
    onSubmit(formValue, deleteLocationIds);
  };

  const handleReset = () => {
    setDefaultLocations();
  };

  const handleCheckAllLocations = (_, checked) => {
    const locationId = checked ? fp.map('value', locationOptions) : [];
    setFormValue({ ...formValue, locationId });
    setCheckAllLocations(checked);
    setIndeterminateLocations(false);
  };

  useEffect(
    () => {
      if (!fp.isEmpty(trackingMethodLocations)) {
        setDefaultLocations();
      }
    },
    [trackingMethodLocations]
  );

  const renderCheckAllTrackingMethod = () => (
    <Checkbox
      inline
      indeterminate={indeterminateLocations}
      checked={checkAllLocations}
      onChange={handleCheckAllLocations}
    >
      Check all
    </Checkbox>
  );

  return (
    <Form
      ref={formRef}
      model={FormModel}
      layout="horizontal"
      formValue={formValue}
      formError={formError}
      onCheck={handleFormCheck}
      onChange={handleFormChange}
      style={{ maxWidth: 482 }}
    >
      <FormGroup style={{ marginBottom: 30 }}>
        <ControlLabel>Available Locations</ControlLabel>
        <FormControl
          name="locationId"
          accepter={CheckPicker}
          data={locationOptions}
          errorMessage={formError.locationId}
          placeholder="Select Locations"
          style={{ width: 300 }}
          renderExtraFooter={renderCheckAllTrackingMethod}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>Starting Location</ControlLabel>
        <FormControl
          name="defaultLocationId"
          accepter={SelectPicker}
          data={availableLocationOptions}
          placeholder="Select Location"
          style={{ width: 300 }}
        />
      </FormGroup>
      <ButtonToolbar style={{ textAlign: 'right', marginTop: 50 }}>
        <Button onClick={handleReset} appearance="default" disabled={isSaving}>
          <Icon icon="refresh" /> Reset
        </Button>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          disabled={isSaving || !fp.isEmpty(formError)}
        >
          <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
    </Form>
  );
};

TrackingMethodLocationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  locationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  trackingMethodLocations: PropTypes.arrayOf(PropTypes.shape({}))
};

TrackingMethodLocationForm.defaultProps = {
  trackingMethodLocations: []
};

export default TrackingMethodLocationForm;
